class LocalizationForm extends HTMLElement {
  connectedCallback() {
    if (this._localizationFormReady) return;
    this._localizationFormReady = true;

    this.elements = {
      input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
      button: this.querySelector('button'),
      panel: this.querySelector('.disclosure_element'),
    };
    if (!this.elements.button || !this.elements.panel) return;

    this.elements.button.addEventListener('click', this.openSelector.bind(this));
    this.addEventListener('focusout', this.closeSelector.bind(this));
    this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

    this.querySelectorAll('a').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));

    this._onScrollReposition = this._onScrollReposition.bind(this);
    const panel = this.elements.panel;
    if (panel.tagName === 'DIALOG') {
      panel.addEventListener('close', () => {
        this.elements.button.setAttribute('aria-expanded', 'false');
        this._clearDialogPosition();
        window.removeEventListener('scroll', this._onScrollReposition, { passive: true });
      });
    }

    this._onResizeGuard = this._onResizeGuard.bind(this);
    window.addEventListener('resize', this._onResizeGuard);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._onResizeGuard);
    window.removeEventListener('scroll', this._onScrollReposition, { passive: true });
  }

  _onScrollReposition() {
    const p = this.elements?.panel;
    if (p?.tagName === 'DIALOG' && p.open) this._positionDialogPanel();
  }

  _onResizeGuard() {
    const p = this.elements?.panel;
    if (p?.tagName !== 'DIALOG') return;
    if (!window.matchMedia('(min-width: 1025px)').matches && p.open) {
      p.close();
    }
  }

  _useTopLayerDialog() {
    const p = this.elements?.panel;
    return (
      p?.tagName === 'DIALOG' &&
      typeof p.showModal === 'function' &&
      window.matchMedia('(min-width: 1025px)').matches &&
      !!this.closest('.header__top-bar')
    );
  }

  _positionDialogPanel() {
    const panel = this.elements.panel;
    const field = this.querySelector('.disclosure-form-field');
    if (!field || panel.tagName !== 'DIALOG' || !panel.open) return;

    const fr = field.getBoundingClientRect();
    const gap = 15;

    panel.style.setProperty('position', 'fixed');
    panel.style.setProperty('margin', '0');
    panel.style.setProperty('inset', 'unset');
    panel.style.setProperty('top', `${Math.round(fr.bottom + gap)}px`);
    panel.style.setProperty('bottom', 'auto');
    panel.style.setProperty('transform', 'none');
    if (panel.classList.contains('toright')) {
      panel.style.setProperty('left', 'auto');
      panel.style.setProperty('right', `${Math.round(document.documentElement.clientWidth - fr.right)}px`);
    } else {
      panel.style.setProperty('right', 'auto');
      panel.style.setProperty('left', `${Math.round(fr.left)}px`);
    }
    panel.style.setProperty('width', 'max-content');
    panel.style.setProperty('max-width', 'min(260px, calc(100vw - 20px))');
  }

  _clearDialogPosition() {
    const panel = this.elements.panel;
    if (panel.tagName !== 'DIALOG') return;
    ['position', 'margin', 'inset', 'top', 'right', 'bottom', 'left', 'width', 'max-width', 'transform'].forEach((k) =>
      panel.style.removeProperty(k)
    );
  }

  hidePanel() {
    if (!this.elements?.button || !this.elements?.panel) return;
    const panel = this.elements.panel;
    if (panel.tagName === 'DIALOG') {
      if (typeof panel.close === 'function' && panel.open) panel.close();
      this.elements.button.setAttribute('aria-expanded', 'false');
      return;
    }
    this.elements.button.setAttribute('aria-expanded', 'false');
    panel.setAttribute('hidden', true);
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;
    this.hidePanel();
    this.elements.button.focus();
  }

  onItemClick(event) {
    event.preventDefault();
    const panel = this.elements.panel;
    if (panel.tagName === 'DIALOG' && panel.open && typeof panel.close === 'function') {
      panel.close();
    }
    const form = this.querySelector('form');
    this.elements.input.value = event.currentTarget.dataset.value;
    if (form) form.submit();
  }

  openSelector() {
    this.elements.button.focus();
    const panel = this.elements.panel;

    if (this._useTopLayerDialog()) {
      if (panel.open) {
        panel.close();
        this.elements.button.setAttribute('aria-expanded', 'false');
      } else {
        panel.showModal();
        this.elements.button.setAttribute('aria-expanded', 'true');
        window.addEventListener('scroll', this._onScrollReposition, { passive: true });
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this._positionDialogPanel());
        });
      }
      return;
    }

    panel.toggleAttribute('hidden');
    this.elements.button.setAttribute(
      'aria-expanded',
      (this.elements.button.getAttribute('aria-expanded') === 'false').toString()
    );
  }

  closeSelector(event) {
    if (event.target?.classList?.contains('country-selector__overlay')) {
      this.hidePanel();
      return;
    }

    /* Top-bar <dialog>: avoid focusout + hidePanel racing showModal / native dialog focus. */
    if (
      this.closest('.header__top-bar') &&
      window.matchMedia('(min-width: 1025px)').matches &&
      this.elements.panel?.tagName === 'DIALOG'
    ) {
      return;
    }

    setTimeout(() => {
      const active = document.activeElement;
      if (!active || !this.contains(active)) {
        this.hidePanel();
      }
    }, 0);
  }
}

customElements.define('localization-form', LocalizationForm);
